import { useTranslation } from 'react-i18next';

import type { CardPhase } from '../../../api/cards';
import type { CardProgressData } from '../../../types';
import type { ThrottleState } from './use-card-batch';

/** Live progress for either streaming phase, with the throttle banner. */
export function BatchProgress({
  phase,
  progress,
  throttled,
  finalizing,
  onCancel,
}: {
  phase: CardPhase;
  progress: CardProgressData;
  throttled: ThrottleState | null;
  /** Every card is rendered; the server is assembling the zip/pdf. */
  finalizing: boolean;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const pct =
    progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  function statusLabel(): string {
    if (finalizing) {
      return t('cardGenerator.finalizing');
    }
    if (!progress.current) {
      return t('cardGenerator.starting');
    }
    if (phase === 'preflighting') {
      return t('cardGenerator.checking', { upn: progress.current });
    }
    return t('cardGenerator.processing', { upn: progress.current });
  }

  return (
    <div>
      <div className="mb-1.5 flex justify-between text-sm text-muted">
        <span>{statusLabel()}</span>
        <span>
          {progress.done} / {progress.total}
        </span>
      </div>
      <div className="h-2 w-full bg-ink/8">
        <div
          className="h-2 bg-blue transition-[width] duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
      {throttled && (
        <div className="mt-2 text-sm text-muted" role="status">
          {t('cardGenerator.throttled', {
            service: throttled.service,
            time: new Date(throttled.resumeAt).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            }),
          })}
        </div>
      )}
      <div className="mt-3">
        <button
          type="button"
          onClick={onCancel}
          className="btn-ghost px-[12px] py-[6px] text-[11px]"
        >
          {t('cardGenerator.cancelRun')}
        </button>
      </div>
    </div>
  );
}
